import React, {useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, UiBox, UiButton, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import TextModal from '~/components/modals/TextModal';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {
  EASE_LINK,
  EMERGENCY_CONTRACEPTION_KEYS,
  LAST_PROTECT_INTERCOURSE,
  TELEHEALTH_DEEPLINK,
} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';
import {redirectInIframe} from '~/utils/helpers';

const Questionnaire5Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const [isShowSorryModal, setIsShowSorryModal] = useState(false);
  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.LAST_UNPROTECTED_INTERCOURSE]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === EMERGENCY_CONTRACEPTION_KEYS.LAST_UNPROTECTED_INTERCOURSE);
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const {inputs, setInputs} = useInput({selected: currentAnswer?.selected || ''});

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({selected: value});
    if (value === LAST_PROTECT_INTERCOURSE.DID_NOT.value) {
      setTimeout(() => {
        window.scrollTo({top: 200, behavior: 'smooth'});
      }, 500);
    }
  };

  const onClickSubmit = async () => {
    if (inputs.selected === LAST_PROTECT_INTERCOURSE.M_4_DAYS.value) {
      setIsShowSorryModal(true);
      return;
    }
    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.LAST_UNPROTECTED_INTERCOURSE,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.LAST_PERIOD_AT}));
  };

  const onClickReturnToMenu = () => {
    if (isWebview) {
      window.open(TELEHEALTH_DEEPLINK, '_self');
    } else {
      redirectInIframe(EASE_LINK, '_blank');
    }
  };

  const renderModalContent = () => (
    <>
      <UiText mt={spacing.small} textAlign="center">
        We cannot prescribe you emergency contraception because the pills we prescribe are only effective up to 5 days
        after unprotected sex and may not reach you in time.
      </UiText>
      <UiText mt={spacing.small} textAlign="center">
        We recommend that you see your regular health provider to discuss your situation.
      </UiText>
    </>
  );
  return (
    <>
      <TextModal
        isShowing={isShowSorryModal}
        onDismissModal={() => setIsShowSorryModal(false)}
        onClickSubmit={onClickReturnToMenu}
        title="We’re sorry"
        buttonTitle="Return to menu"
      >
        {renderModalContent()}
      </TextModal>
      <UiQuestionnaireLayout
        backIcon
        handleOnNext={onClickSubmit}
        disableNext={!inputs.selected}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
          <UiText fontSize="lg" fontWeight="bold" mt={spacing.large}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiBox mt={spacing.medium} w="100%">
            <UiSimpleGrid columns={{base: 1, lg: 2}} spacing={5}>
              {questionnaire?.options?.map(value => (
                <ScrollToBottomButton
                  key={`${value.label}-${value.value}`}
                  title={value.label}
                  variant={inputs.selected === value.value ? 'solid' : 'toggle'}
                  onClick={() => onClickOption(value)}
                />
              ))}
            </UiSimpleGrid>
            {/* {inputs.selected === LAST_PROTECT_INTERCOURSE.DID_NOT.value ? (
              <UiText mt={spacing.small} textAlign="center" mx={spacing.tiny}>
                Please note that we are unable to prescribe emergency contraception unless you’ve had unprotected sex
                recently
              </UiText>
            ) : (
              <UiBox pt={1} />
            )} */}
          </UiBox>
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire5Screen;
